<template>
  <div class="position-fixed bottom-1 end-1 z-index-2">
    <!-- Expand/Collapse Button -->
    <div class="d-flex justify-content-center position-relative">
      <button
        v-if="!animationInProgress || notificationsVisible"
        class="btn btn-primary"
        style="position: relative;"
        @click="toggleNotifications"
      >
        <i :class="notificationsVisible ? 'fas fa-chevron-down' : 'fas fa-chevron-up'"></i>
        <!-- Message Count Badge -->
        <span v-if="notifications.length > 0" class="notification-badge">
          {{ notifications.length }}
        </span>
      </button>
    </div>

    <transition
      name="slide-notifications"
      @after-enter="onAnimationEnd"
      @after-leave="onAnimationEnd"
    >
      <div v-if="notificationsVisible" class="notifications-container">
        <!-- Tabs -->
        <div
          class="notifications-tabs px-2 py-1"
          style="font-size: 0.85rem; font-weight: 600; display: flex; justify-content: space-around; border-bottom: 1px solid #dee2e6;"
        >
          <button
            :class="['tab-button', activeTab === 'renewal' ? 'active-tab' : '']"
            @click="activeTab = 'renewal'"
          >
            Renewals
          </button>
          <button
            :class="['tab-button', activeTab === 'note' ? 'active-tab' : '']"
            @click="activeTab = 'note'"
          >
            Reminders
          </button>
          <button
            :class="['tab-button', activeTab === 'support' ? 'active-tab' : '']"
            @click="activeTab = 'support'"
          >
            Support
          </button>
        </div>

        <!-- Renewals / Reminders -->
        <div v-if="activeTab === 'renewal' || activeTab === 'note'">
          <div class="notifications-list">
            <argon-snackbar
              v-for="(notification, index) in filteredNotifications"
              :key="notification.notification_id"
              :title="notification.title"
              :date="notification.date"
              :description="notification.description"
              :icon="notification.icon"
              :color="notification.color"
              :proposal_id="notification.proposal_id"
              :notification_id="notification.notification_id"
              :close-handler="() => removeNotification(index, notification.notification_id)"
            />
            <div v-if="filteredNotifications.length === 0" class="no-notifications-message">
              <p>You have no notifications.</p>
            </div>
          </div>
        </div>

        <!-- Support Chat -->
        <div v-if="activeTab === 'support'" class="support-chat-wrap">
          <!--div class="support-chat-header">
            <strong>{{ supportChatTitle }}</strong>
            <span v-if="chatLoading" class="small text-muted ms-2">syncing…</span>
          </div-->

          <div ref="chatBody" class="support-chat-body">
            <template v-if="chatError">
              <div class="alert alert-danger p-2 my-2">{{ chatError }}</div>
            </template>

            <template v-else>
              <div
                v-for="(m, i) in chatMessages"
                :key="i"
                :class="['chat-bubble', m.isMe ? 'me' : 'them']"
              >
                <div class="text">{{ m.text }}</div>
                <div class="meta">{{ friendlyTime(m.ts) }}</div>
              </div>
            </template>
          </div>

          <div class="support-chat-input">
            <div class="input-group">
              <input
                v-model.trim="chatInput"
                type="text"
                class="form-control"
                placeholder="Type a message…"
                :disabled="sending"
                @keyup.enter="sendSupportMessage"
              />
              <button
                class="btn btn-primary send-btn"
                :disabled="sending || chatInput.length === 0"
                title="Send"
                @click="sendSupportMessage"
              >
                <i class="fas fa-paper-plane"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import ArgonSnackbar from '@/components/ArgonSnackbar.vue';
import axios from 'axios';

export default {
  name: 'NotificationManager',
  components: { ArgonSnackbar },
  data() {
    return {
      activeTab: 'renewal',
      notifications: [],
      animationInProgress: false,

      // --- Support chat state (inline) ---
      supportChatId: null,               // if null, show greeting + allow starting chat
      supportChatTitle: 'Support Chat',
      chatMessages: [],                  // [{ text, ts: Date, isMe }]
      chatInput: '',
      chatLoading: false,
      chatError: null,
      pollSeconds: 6,
      pollTimer: null,
      sending: false,

      initialGreeting: 'How can we help today?', // matches iOS
      enableMocks: false, // set true to force mock (otherwise auto-fallback when API fails)
    };
  },
  computed: {
    notificationsVisible() {
      return this.$store.getters['notifications/notificationsVisible'];
    },
    filteredNotifications() {
      if (this.activeTab === 'renewal') {
        return this.notifications.filter(n => n.category === 'renewal_reminder');
      } else if (this.activeTab === 'note') {
        return this.notifications.filter(n => n.category === 'note_reminder');
      }
      return [];
    },
    shouldPollSupport() {
      return (
        this.notificationsVisible &&
        this.activeTab === 'support' &&
        document.visibilityState === 'visible' &&
        !!this.supportChatId
      );
    },
  },
  watch: {
    notificationsVisible() {
      this._syncPolling();
    },
    activeTab(newVal) {
      if (newVal === 'support' && !this.supportChatId) {
        this._ensureGreeting();
      }
      this._syncPolling();
    },
  },
  created() {
    this.$store.dispatch('notifications/initializeNotificationsVisibility');
    this.fetchNotifications();
    document.addEventListener('visibilitychange', this._syncPolling);
  },
  beforeUnmount() {
    this._stopPolling();
    document.removeEventListener('visibilitychange', this._syncPolling);
  },
  methods: {
    toggleNotifications() {
      this.animationInProgress = true;
      this.$store.dispatch('notifications/toggleNotificationsVisibility');
    },
    onAnimationEnd() {
      this.animationInProgress = false;
      this._syncPolling();
    },

    // ===== notifications (renewals/reminders) =====
    async fetchNotifications() {
      const token = window.__protegoGetAccessToken ? window.__protegoGetAccessToken() : null;
      if (!token && !this.enableMocks) return;

      try {
        if (this.enableMocks) {
          const mock = await this._mockFetchNotifications();
          this._ingestNotificationsPayload(mock);
          return;
        }

        const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/notifications`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        this._ingestNotificationsPayload(response.data);
      } catch (error) {
        console.warn('Notifications API failed, using mock:', error);
        const mock = await this._mockFetchNotifications();
        this._ingestNotificationsPayload(mock);
      }
    },
    _ingestNotificationsPayload(api) {
      // support array or { items: [...], support_chat: { id, title? } }
      const items = Array.isArray(api) ? api : api?.items || [];
      const sorted = [...items].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      this.notifications = sorted.map(n => ({
        title: n.title,
        date: n.created_at ? new Date(n.created_at).toLocaleString() : 'Just now',
        description: n.message,
        icon: { component: 'ni ni-bell-55', color: 'info' },
        color: 'info',
        proposal_id: n.proposal_id,
        notification_id: n.id,
        category: n.category,
      }));

      const sc = api?.support_chat || null;
      this.supportChatId = sc?.id ?? null;
      this.supportChatTitle = sc?.title || 'Support Chat';

      if (this.activeTab === 'support' && !this.supportChatId) {
        this._ensureGreeting();
      }
      if (this.activeTab === 'support' && this.supportChatId) {
        this._pollOnce();
      }
    },
    async removeNotification(index, notificationId) {
      try {
        if (this.enableMocks) {
          this.notifications.splice(index, 1);
          return;
        }
        const token = window.__protegoGetAccessToken ? window.__protegoGetAccessToken() : null;
        await axios.put(
          `${process.env.VUE_APP_API_BASE_URL}/notifications/${notificationId}/dismiss`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        this.notifications.splice(index, 1);
      } catch (error) {
        console.error('Error dismissing notification:', error);
      }
    },

    // ===== support chat (inline) =====
    _ensureGreeting() {
      // Add exactly one greeting bubble (non-dup) when no chat exists yet
      const hasGreeting = this.chatMessages.some(
        m => !m.isMe && m.text === this.initialGreeting
      );
      if (!hasGreeting) {
        this.chatMessages = [
          { text: this.initialGreeting, ts: new Date(), isMe: false },
          ...this.chatMessages,
        ];
        this.$nextTick(this._scrollToBottom);
      }
    },
    _syncPolling() {
      if (this.shouldPollSupport) {
        this._startPolling();
      } else {
        this._stopPolling();
      }
    },
    _startPolling() {
      if (this.pollTimer) return;
      this._pollOnce();
      this.pollTimer = setInterval(this._pollOnce, this.pollSeconds * 1000);
    },
    _stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    async _pollOnce() {
      if (!this.supportChatId) return;

      try {
        this.chatLoading = true;
        this.chatError = null;

        let data;
        if (this.enableMocks) {
          data = await this._mockPollChat(this.supportChatId);
        } else {
          const token = window.__protegoGetAccessToken ? window.__protegoGetAccessToken() : null;
          const resp = await axios.get(
            `${process.env.VUE_APP_API_BASE_URL}/chats/${this.supportChatId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          data = resp.data;
        }

        const msgs = (data?.messages || []).map(m => ({
          text: String(m.message ?? ''),
          ts: m.created_at ? new Date(m.created_at) : new Date(),
          isMe: !!m.is_me,
        }));

        const wasAtBottom = this._isScrolledToBottom();
        this.chatMessages = msgs.length ? msgs : this.chatMessages;
        if (!msgs.length && !this.supportChatId) {
          this._ensureGreeting();
        }
        this.$nextTick(() => {
          if (wasAtBottom) this._scrollToBottom();
        });
      } catch (e) {
        this.chatError = 'Failed to sync messages';
        console.error(e);
      } finally {
        this.chatLoading = false;
      }
    },
    async sendSupportMessage() {
      const text = this.chatInput.trim();
      if (!text) return;

      // optimistic append
      const now = new Date();
      this.chatMessages.push({ text, ts: now, isMe: true });
      this.chatInput = '';
      this.$nextTick(this._scrollToBottom);

      try {
        this.sending = true;

        if (!this.supportChatId) {
          // start new chat
          let newId;
          if (this.enableMocks) {
            const data = await this._mockStartChat(text);
            newId = data?.chat?.id;
          } else {
            const token = window.__protegoGetAccessToken ? window.__protegoGetAccessToken() : null;
            const { data } = await axios.post(
              `${process.env.VUE_APP_API_BASE_URL}/chats`,
              { message: text },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
              }
            );
            newId = data?.chat?.id;
          }

          if (newId) {
            this.supportChatId = newId;
            await this._pollOnce();
            this._syncPolling();
          }
        } else {
          // send in existing chat
          if (this.enableMocks) {
            await this._mockSendMessage(this.supportChatId, text);
          } else {
            const token = window.__protegoGetAccessToken ? window.__protegoGetAccessToken() : null;
            await axios.post(
              `${process.env.VUE_APP_API_BASE_URL}/chats/${this.supportChatId}/messages`,
              { message: text },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
              }
            );
          }
          // quick reconcile
          await this._pollOnce();
        }
      } catch (e) {
        console.error('sendSupportMessage failed:', e);
        // leave optimistic bubble; next poll will reconcile
      } finally {
        this.sending = false;
      }
    },

    _isScrolledToBottom() {
      const el = this.$refs.chatBody;
      if (!el) return true;
      const threshold = 24;
      return el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
    },
    _scrollToBottom() {
      const el = this.$refs.chatBody;
      if (!el) return;
      el.scrollTop = el.scrollHeight;
    },
    friendlyTime(ts) {
      const d = ts instanceof Date ? ts : new Date(ts);
      const diff = (Date.now() - d.getTime()) / 1000;
      if (diff < 60) return 'now';
      if (diff < 3600) return `${Math.floor(diff / 60)}m`;
      if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
      return `${Math.floor(diff / 86400)}d`;
    },

    // ===== minimal mocks (only used if enableMocks=true or API fails) =====
    async _mockFetchNotifications() {
      // Simulate { items: [...], support_chat: { id?, title? } }
      return new Promise(resolve => {
        setTimeout(() => {
          resolve({
            items: [
              {
                id: 1001,
                title: 'Proposal Approved',
                message: 'Your proposal CC-1042 was approved.',
                created_at: new Date(Date.now() - 12 * 60 * 1000).toISOString(),
                category: 'renewal_reminder',
              },
              {
                id: 1002,
                title: 'Document Added',
                message: 'Acme, Inc. uploaded Coverage_Addendum.pdf',
                created_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
                category: 'note_reminder',
              },
            ],
            // Flip between null or an object to test both paths
            support_chat: null, // or { id: 999, title: 'Support Chat' }
          });
        }, 200);
      });
    },
    async _mockPollChat() {
      return new Promise(resolve => {
        setTimeout(() => {
          // Return a tiny static history
          resolve({
            messages: [
              {
                message: 'How can we help today?',
                created_at: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
                is_me: false,
              },
              {
                message: 'Need a copy of my policy.',
                created_at: new Date(Date.now() - 4 * 60 * 1000).toISOString(),
                is_me: true,
              },
              {
                message: 'You got it—sending now.',
                created_at: new Date(Date.now() - 3 * 60 * 1000).toISOString(),
                is_me: false,
              },
            ],
          });
        }, 200);
      });
    },
    async _mockStartChat(firstMsg) {
      return new Promise(resolve => {
        setTimeout(() => {
          resolve({
            ok: true,
            chat: {
              id: 999,
              messages: [
                { message: firstMsg, created_at: new Date().toISOString(), is_me: true },
              ],
            },
          });
        }, 200);
      });
    },
    async _mockSendMessage() {
      return new Promise(resolve => setTimeout(() => resolve({ ok: true }), 150));
    },
  },
};
</script>

<style scoped>
.notification-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: red;
  color: white;
  border-radius: 50%;
  padding: 2px 6px;
  font-size: 0.75rem;
  font-weight: bold;
}
.notifications-container {
  width: 390px;
  max-height: 600px;
  overflow: hidden;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  padding: 10px;
  background: #fff;
}
.notifications-tabs .tab-button {
  flex: 1;
  padding: 8px 0;
  font-size: 0.85rem;
  font-weight: 600;
  color: #6c757d;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  transition: all 0.3s ease;
  cursor: pointer;
}
.notifications-tabs .tab-button:hover {
  color: #007bff;
}
.active-tab {
  color: #007bff;
  border-bottom: 2px solid #007bff;
}
.notifications-list {
  height: 450px;
  overflow-y: auto;
  padding: 4px;
}
.no-notifications-message {
  padding: 8px;
  font-size: 0.85rem;
  color: #999;
  text-align: center;
}
.slide-notifications-enter-active,
.slide-notifications-leave-active {
  transition: all 0.4s ease;
}
.slide-notifications-enter-from,
.slide-notifications-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
.slide-notifications-enter-to,
.slide-notifications-leave-from {
  transform: translateY(0);
  opacity: 1;
}

/* --- Support chat inline styles --- */
.support-chat-wrap {
  display: flex;
  flex-direction: column;
  height: 500px;
}
.support-chat-header {
  display: flex;
  align-items: center;
  padding: 6px 4px 8px;
  border-bottom: 1px solid #eee;
}
.support-chat-body {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  background: #fafbfc;
}
.support-chat-input {
  padding-top: 8px;
  border-top: 1px solid #eee;
}

/* make input & button perfectly flush */
.support-chat-input .input-group > .form-control {
  height: 42px;                /* same height */
  border-top-right-radius: 0;  /* square where they meet */
  border-bottom-right-radius: 0;
}

.support-chat-input .input-group > .send-btn {
  height: 42px;                /* match input height */
  border-top-left-radius: 0;   /* square where they meet */
  border-bottom-left-radius: 0;
  padding-inline: 14px;        /* comfy width for the icon */
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* bubbles */
.chat-bubble {
  max-width: 70%;
  padding: 10px 12px;
  margin: 6px 0;
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(0,0,0,.06);
  word-break: break-word;
}
.chat-bubble .text { white-space: pre-wrap; }
.chat-bubble .meta {
  font-size: 10px; opacity: .6; margin-top: 6px;
}
.chat-bubble.them {
  background: #eef4ff;
  color: #0b1b3a;
  border-bottom-left-radius: 4px;
}
.chat-bubble.me {
  margin-left: auto;
  background: #1E3C72;
  color: #fff;
  border-bottom-right-radius: 4px;
}

/* composer polish */
.composer-input {
  height: 40px;
  border-radius: 8px;
}
.composer-send {
  width: 42px;
  height: 40px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>
